<script setup lang="ts">
import { useLocalStorage, useDocumentVisibility, useOnline } from '@vueuse/core';

const { canInstall, install, showIosInstallGuide } = usePwaInstall();
const installDismissed = useLocalStorage('pwa-install-dismissed', false);
const iosInstallDismissed = useLocalStorage('pwa-ios-install-dismissed', false);

const { $viewport } = useNuxtApp();
const viewportReady = ref(false);
const desktop = computed(() => viewportReady.value && $viewport.isGreaterOrEquals('lg'));
onMounted(() => {
  viewportReady.value = true;
});

const isOnline = useOnline();

const mobileNavOpen = useState('mobileNavOpen', () => false);

const subscriptionRequiredModal = useSubscriptionRequiredModal();
const pushNotificationPrompt = usePushNotificationPrompt();
const { isPushSubscribed, isPushSupported, checkPushSupport, checkPushSubscription } = usePushNotifications();
const { data: session } = useAuth();
const { isGranted } = useIsGranted();

const checkPushNotificationPrompt = async () => {
  if (!isGranted('ROLE_ORG_ADMIN')) return;
  if (!session.value?.showPushNotificationPrompt) return;
  if (!isOnline.value) return;
  checkPushSupport();
  await checkPushSubscription();
  if (isPushSupported.value && !isPushSubscribed.value) {
    setTimeout(() => {
      pushNotificationPrompt.value.show = true;
    }, 2000);
  }
};

const visibility = useDocumentVisibility();
watch(visibility, (current) => {
  if (current === 'visible' && session.value) {
    checkPushNotificationPrompt();
  }
});

watch(
  session,
  async (newSession, oldSession) => {
    if (newSession && !oldSession) {
      if (isGranted('ROLE_ORG_ADMIN')) {
        await checkPushSubscription();
      }
      await checkPushNotificationPrompt();
    }
  },
  { immediate: true },
);
</script>

<template>
  <div class="flex relative">
    <div
      v-if="!desktop && mobileNavOpen"
      class="absolute left-0 top-0 h-screen w-full bg-gray-900 opacity-50 z-20"
      style="height: 100dvh"
      @click="mobileNavOpen = false"
    />
    <AppSideNav />
    <div class="@container flex flex-col flex-grow overflow-y-auto" style="height: 100dvh; padding-top: env(safe-area-inset-top)">
      <AppImpersonationBanner />
      <div v-if="session && !desktop" class="fixed right-8 z-20 my-4 flex items-center gap-5" style="top: max(0.5rem, env(safe-area-inset-top))">
        <AppCartIndicator />
        <AppPermanentMessagesIndicator />
        <UButton icon="heroicons:bars-3-16-solid" class="text-xl" variant="soft" @click="mobileNavOpen = !mobileNavOpen" />
      </div>
      <AppHeader :desktop="desktop" />
      <AppContent class="grow">
        <AppBreadcrumbs />
        <AppPermanentMessages />
        <AppAlert />
        <slot />
      </AppContent>
      <AppFooter />
      <PaymentSubscriptionRequiredModal v-model="subscriptionRequiredModal.show" />
      <PushNotificationPromptModal v-model="pushNotificationPrompt.show" />
      <CookieConsent class="bottom-0" />
    </div>
  </div>
  <AppSpotlight />

  <!-- Offline Banner -->
  <Transition
    enter-from-class="-translate-y-full opacity-0"
    enter-to-class="translate-y-0 opacity-100"
    enter-active-class="transition-all duration-300"
    leave-from-class="translate-y-0 opacity-100"
    leave-to-class="-translate-y-full opacity-0"
    leave-active-class="transition-all duration-300"
  >
    <div
      v-if="!isOnline"
      class="fixed top-0 left-0 right-0 bg-warning text-inverted text-sm font-medium text-center py-2 z-50 flex items-center justify-center gap-2"
    >
      <UIcon name="heroicons:wifi-off" />
      {{ $t('offline.banner') }}
    </div>
  </Transition>

  <!-- PWA Install Banner -->
  <Transition
    enter-from-class="translate-y-full opacity-0"
    enter-to-class="translate-y-0 opacity-100"
    enter-active-class="transition-all duration-300"
    leave-from-class="translate-y-0 opacity-100"
    leave-to-class="translate-y-full opacity-0"
    leave-active-class="transition-all duration-300"
  >
    <div
      v-if="canInstall && !installDismissed"
      class="fixed bottom-4 left-4 right-4 bg-primary-600 text-white p-4 rounded-lg shadow-lg flex justify-between items-center z-50"
    >
      <span class="text-sm">{{ $t('pwa.installPrompt') }}</span>
      <div class="flex gap-2 ml-4">
        <UButton variant="ghost" color="neutral" @click="installDismissed = true">
          {{ $t('pwa.later') }}
        </UButton>
        <UButton color="neutral" @click="void install()">
          {{ $t('pwa.install') }}
        </UButton>
      </div>
    </div>
  </Transition>

  <!-- iOS App Store Banner -->
  <Transition
    enter-from-class="translate-y-full opacity-0"
    enter-to-class="translate-y-0 opacity-100"
    enter-active-class="transition-all duration-300"
    leave-from-class="translate-y-0 opacity-100"
    leave-to-class="translate-y-full opacity-0"
    leave-active-class="transition-all duration-300"
  >
    <div
      v-if="showIosInstallGuide && !iosInstallDismissed"
      class="fixed bottom-4 left-4 right-4 bg-primary-600 text-white p-4 rounded-lg shadow-lg z-50"
    >
      <div class="flex justify-between items-start gap-4">
        <div class="flex-1">
          <p class="text-sm font-medium mb-1">{{ $t('pwa.ios.title') }}</p>
          <p class="text-xs opacity-90">{{ $t('pwa.ios.instructions') }}</p>
        </div>
        <div class="flex items-center gap-2 shrink-0">
          <UButton
            color="white"
            variant="solid"
            size="sm"
            icon="i-heroicons-arrow-top-right-on-square"
            :label="$t('pwa.ios.appStoreButton')"
            as="a"
            :href="APP_STORE_URLS.ios"
            target="_blank"
          />
          <UButton variant="ghost" color="neutral" icon="i-heroicons-x-mark" size="sm" @click="iosInstallDismissed = true" />
        </div>
      </div>
    </div>
  </Transition>
</template>
